import { useEffect, useRef, useState } from "react";
import "./App.css";

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.16 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "reveal-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function App() {
  const canvasRef = useRef(null);
  const trailRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const trailCanvas = trailRef.current;
    const ctx = canvas.getContext("2d");
    const tctx = trailCanvas.getContext("2d");

    let animationId;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const stars = [];
    const nebulaClouds = [];
    const shootingTrail = [];

    const mouse = {
      x: width / 2,
      y: height / 2,
      moved: false,
    };

    function random(min, max) {
      return Math.random() * (max - min) + min;
    }

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      trailCanvas.width = width;
      trailCanvas.height = height;
      createScene();
    }

    function createScene() {
      stars.length = 0;
      nebulaClouds.length = 0;

      for (let i = 0; i < 180; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.7 + 0.2,
          alpha: Math.random() * 0.55 + 0.2,
          twinkle: Math.random() * 0.02 + 0.003,
          offset: Math.random() * Math.PI * 2,
        });
      }

      for (let i = 0; i < 5; i++) {
        nebulaClouds.push({
          x: random(0, width),
          y: random(0, height),
          radius: random(220, 430),
          color: i % 2 === 0 ? "34, 211, 238" : "124, 58, 237",
          alpha: random(0.04, 0.1),
        });
      }
    }

    function drawBackground() {
      const bg = ctx.createLinearGradient(0, 0, 0, height);
      bg.addColorStop(0, "#030712");
      bg.addColorStop(0.45, "#081126");
      bg.addColorStop(1, "#12071d");

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
    }

    function drawNebula() {
      nebulaClouds.forEach((cloud) => {
        const g = ctx.createRadialGradient(
          cloud.x,
          cloud.y,
          0,
          cloud.x,
          cloud.y,
          cloud.radius
        );
        g.addColorStop(0, `rgba(${cloud.color}, ${cloud.alpha})`);
        g.addColorStop(0.45, `rgba(${cloud.color}, ${cloud.alpha * 0.45})`);
        g.addColorStop(1, `rgba(${cloud.color}, 0)`);

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cloud.x, cloud.y, cloud.radius, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function drawStars(time) {
      stars.forEach((star) => {
        const pulse = 0.22 + Math.sin(time * star.twinkle + star.offset) * 0.18;
        const alpha = Math.max(0.08, star.alpha + pulse);

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(219, 234, 254, ${alpha})`;
        ctx.fill();

        if (star.r > 1.1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.r * 3.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(103, 232, 249, ${alpha * 0.08})`;
          ctx.fill();
        }
      });
    }

    function drawCursorGlow() {
      if (!mouse.moved) return;

      const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 180);
      glow.addColorStop(0, "rgba(34, 211, 238, 0.11)");
      glow.addColorStop(0.55, "rgba(124, 58, 237, 0.08)");
      glow.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(mouse.x, mouse.y, 180, 0, Math.PI * 2);
      ctx.fill();
    }

    function updateTrail() {
      if (mouse.moved) {
        shootingTrail.push({
          x: mouse.x,
          y: mouse.y,
          life: 1,
        });
      }

      for (let i = shootingTrail.length - 1; i >= 0; i--) {
        shootingTrail[i].life -= 0.035;
        if (shootingTrail[i].life <= 0) shootingTrail.splice(i, 1);
      }
    }

    function drawTrail() {
      tctx.clearRect(0, 0, width, height);

      for (let i = 1; i < shootingTrail.length; i++) {
        const prev = shootingTrail[i - 1];
        const curr = shootingTrail[i];
        const alpha = curr.life * 0.95;

        const grad = tctx.createLinearGradient(prev.x, prev.y, curr.x, curr.y);
        grad.addColorStop(0, `rgba(124, 58, 237, 0)`);
        grad.addColorStop(0.35, `rgba(124, 58, 237, ${alpha * 0.45})`);
        grad.addColorStop(1, `rgba(34, 211, 238, ${alpha})`);

        tctx.strokeStyle = grad;
        tctx.lineWidth = Math.max(1, curr.life * 5);
        tctx.lineCap = "round";

        tctx.beginPath();
        tctx.moveTo(prev.x, prev.y);
        tctx.lineTo(curr.x, curr.y);
        tctx.stroke();
      }

      if (shootingTrail.length > 0) {
        const head = shootingTrail[shootingTrail.length - 1];

        tctx.beginPath();
        tctx.arc(head.x, head.y, 3.2, 0, Math.PI * 2);
        tctx.fillStyle = "rgba(255,255,255,0.95)";
        tctx.fill();

        tctx.beginPath();
        tctx.arc(head.x, head.y, 10, 0, Math.PI * 2);
        tctx.fillStyle = "rgba(34, 211, 238, 0.18)";
        tctx.fill();
      }
    }

    function animate(time) {
      drawBackground();
      drawNebula();
      drawStars(time * 0.05);
      drawCursorGlow();
      updateTrail();
      drawTrail();
      animationId = requestAnimationFrame(animate);
    }

    function onMouseMove(e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.moved = true;
    }

    function onTouchMove(e) {
      if (e.touches.length > 0) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
        mouse.moved = true;
      }
    }

    resize();
    animate(0);

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove", onTouchMove, { passive: true });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  return (
    <div className="app">
      <canvas ref={canvasRef} className="space-canvas" />
      <canvas ref={trailRef} className="trail-canvas" />

      <div className="planet planet-main" />
      <div className="planet-ring" />
      <div className="planet-glow" />

      <header className="header">
        <div className="header-actions">
          <a
            href="https://t.me/TemirkhanSaidov"
            target="_blank"
            rel="noreferrer"
            className="btn btn-fill"
          >
            Telegram
          </a>
          <a href="#contact" className="btn btn-fill btn-fill-dark">
            Связаться
          </a>
        </div>

        <div className="brand-center">
          <div className="logo">SAIDOV AUTOMATION</div>
        </div>
      </header>

      <main className="container">
        <section className="hero">
          <Reveal delay={0}>
            <div className="hero-text">
              <h1>Создаю сайты, ботов и приложения для бизнеса</h1>

              <p>
                Помогаю бизнесу получать заявки через современные сайты,
                Telegram-ботов и автоматизацию. Разрабатываю удобные решения
                с красивым интерфейсом и акцентом на результат.
              </p>

              <div className="buttons hero-buttons">
                <a
                  href="https://t.me/TemirkhanSaidov"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-fill"
                >
                  Написать в Telegram
                </a>

                <a href="#services" className="btn btn-fill btn-fill-dark">
                  Мои услуги
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="glass-card dashboard-card cosmic-card">
              <div className="card-top">
                <span></span>
                <span></span>
                <span></span>
              </div>

              <div className="dashboard-title">Cosmic Dashboard</div>
              <div className="dashboard-big">
                Дизайн, автоматизация и digital-решения
              </div>

              <div className="mini-grid">
                <div className="mini-box active">Telegram-боты</div>
                <div className="mini-box active">Автозаявки</div>
                <div className="mini-box active">Сайты</div>
                <div className="mini-box active">Android</div>
              </div>
            </div>
          </Reveal>
        </section>

        <section id="services" className="cards">
          <Reveal delay={0}>
            <div className="card cosmic-card service-card">
              <h3>Сайты</h3>
              <p>Лендинги, сайты услуг, визитки и продающие страницы для бизнеса.</p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="card cosmic-card service-card">
              <h3>Telegram-боты</h3>
              <p>Боты для заявок, меню, уведомлений, консультаций и автоматизации.</p>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className="card cosmic-card service-card">
              <h3>Автоматизация</h3>
              <p>Интеграции, формы, заявки, уведомления и настройка рабочих процессов.</p>
            </div>
          </Reveal>

          <Reveal delay={240}>
            <div className="card cosmic-card service-card">
              <h3>Android-приложения</h3>
              <p>Простые приложения и интерфейсы под задачи бизнеса.</p>
            </div>
          </Reveal>
        </section>

        <section className="contact-grid">
          <Reveal delay={0}>
            <div id="contact" className="card large glow cosmic-card contact-card">
              <h2>Контакты</h2>
              <p>Telegram: @TemirkhanSaidov</p>
              <p>Телефон: +79389969111</p>
              <p>Email: temirkhan-95@mail.ru</p>

              <div className="buttons">
                <a
                  href="https://t.me/TemirkhanSaidov"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-fill"
                >
                  Написать
                </a>

                <a href="tel:+79389969111" className="btn btn-fill btn-fill-dark">
                  Позвонить
                </a>
              </div>
            </div>
          </Reveal>
        </section>
      </main>
    </div>
  );
}